import { useEffect, useRef, useCallback, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, XCircle, Info, X } from 'lucide-react';
import styles from './Toast.module.css';

/* ---- Toast types ---- */

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastData {
  id: string;
  variant: ToastVariant;
  message: string;
  description?: string;
  duration?: number;
}

/* ---- Simple external store (no Zustand needed) ---- */

type Listener = () => void;

let toasts: ToastData[] = [];
const listeners = new Set<Listener>();

function emit() {
  listeners.forEach((l) => l());
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return toasts;
}

let nextId = 0;

// eslint-disable-next-line react-refresh/only-export-components -- imperative toast() API lives with its renderer
export function toast(
  variant: ToastVariant,
  message: string,
  options?: { description?: string; duration?: number },
) {
  const id = String(++nextId);
  const entry: ToastData = {
    id,
    variant,
    message,
    description: options?.description,
    duration: options?.duration ?? 5000,
  };
  toasts = [...toasts, entry];
  emit();
  return id;
}

toast.success = (msg: string, opts?: { description?: string; duration?: number }) =>
  toast('success', msg, opts);
toast.error = (msg: string, opts?: { description?: string; duration?: number }) =>
  toast('error', msg, opts);
toast.info = (msg: string, opts?: { description?: string; duration?: number }) =>
  toast('info', msg, opts);

function removeToast(id: string) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

/* ---- Individual Toast ---- */

const icons: Record<ToastVariant, typeof CheckCircle> = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
};

function ToastItem({ data, onRemove }: { data: ToastData; onRemove: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleClose = useCallback(() => {
    ref.current?.classList.add(styles.exiting);
    setTimeout(onRemove, 150);
  }, [onRemove]);

  useEffect(() => {
    if (data.duration && data.duration > 0) {
      timerRef.current = setTimeout(handleClose, data.duration);
    }
    return () => clearTimeout(timerRef.current);
  }, [data.duration, handleClose]);

  const Icon = icons[data.variant];
  const toastClass = [styles.toast, styles[data.variant]].join(' ');

  return (
    <div ref={ref} className={toastClass} role="alert">
      <Icon size={18} strokeWidth={1.5} className={styles.icon} />
      <div className={styles.content}>
        <p className={styles.message}>{data.message}</p>
        {data.description && (
          <p className={styles.description}>{data.description}</p>
        )}
      </div>
      <button className={styles.closeBtn} onClick={handleClose} aria-label="Fechar">
        <X size={14} strokeWidth={1.5} />
      </button>
    </div>
  );
}

/* ---- Toast Container (mount once in App) ---- */

export function ToastContainer() {
  const items = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  if (items.length === 0) return null;

  return createPortal(
    <div className={styles.container} aria-live="polite">
      {items.map((t) => (
        <ToastItem key={t.id} data={t} onRemove={() => removeToast(t.id)} />
      ))}
    </div>,
    document.body,
  );
}
